import { asArray, asNumber, ifNumeric } from '#library/coercion.library.js';
/**
 * Maximum 32-bit signed integer timer delay supported by Node.js and the HTML timer specification (2^31 - 1).
 */
export const TIMEOUT_MAX = 2 ** 31 - 1;
/**
 * Converts a number or array of numbers into a contiguous hexadecimal string.
 * Flattens nested arrays and filters out non-integers.
 *
 * @param num - The number(s) to convert
 * @param len - Optional maximum length of the resulting string
 * @returns The hexadecimal string representation
 * @example
 * ```ts
 * toHex([255, 16]); // 'ff10'
 * ```
 */
export const toHex = (num = [], len) => asArray(num) // ensure array
    .flat(1_000_000) // flatten any arrays to arbitrary depth
    .filter(Number.isInteger) // ensure integers	
    .map(val => (val + 0x100).toString(16).slice(-2))
    .join('')
    .toLowerCase()
    .substring(0, len ?? Number.MAX_SAFE_INTEGER);
/**
 * Appends an ordinal suffix (st, nd, rd, th) to a number.
 *
 * @param idx - The number to format (defaults to 0)
 * @returns The number formatted as a string with its ordinal suffix
 * @example
 * ```ts
 * suffix(1); // '1st'
 * suffix(23); // '23rd'
 * ```
 */
export const suffix = (idx = 0) => {
    const n = Math.abs(idx);
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13)
        return `${idx}th`;
    switch (n % 10) {
        case 1:
            return `${idx}st`;
        case 2:
            return `${idx}nd`;
        case 3:
            return `${idx}rd`;
        default:
            return `${idx}th`;
    }
};
export function split(nbr, chr = '.', zero = true) {
    return nbr?.toString().split(chr).map(val => ifNumeric(val, zero))
        || [];
}
/**
 * Formats a number to a fixed number of decimal places.
 *
 * @param nbr - The number or string to format
 * @param max - The maximum number of decimal places (default: 2)
 * @returns The fixed-precision string representation
 * @example
 * ```ts
 * fix(12.3456, 2); // '12.35'
 * ```
 */
export const fix = (nbr = 0, max = 2) => asNumber(nbr, 0).toFixed(max);
/**
 * Removes the colon from an HH:MI time string and returns it as a number.
 *
 * @param hhmi - The time string (e.g., '14:30')
 * @returns The numeric representation (e.g., 1430)
 * @example
 * ```ts
 * asTime('14:30'); // 1430
 * ```
 */
export const asTime = (hhmi) => Number(String(hhmi).replace(':', ''));
const ROMAN_MATRIX = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
];
const ROMAN_VALUES = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000,
};
const RE_ROMAN_NUMERALS = /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;
/**
 * Converts a positive integer into its Roman numeral string representation.
 *
 * @param num - An integer from 1 through 3999
 * @returns Uppercase subtractive Roman numerals, or an empty string for non-integers or values outside that range
 * @example
 * ```ts
 * toRoman(2026); // 'MMXXVI'
 * toRoman(14); // 'XIV'
 * ```
 */
export function toRoman(num) {
    if (!Number.isInteger(num) || num < 1 || num > 3999)
        return '';
    let n = num;
    let result = '';
    for (const [value, symbol] of ROMAN_MATRIX) {
        while (n >= value) {
            result += symbol;
            n -= value;
        }
    }
    return result;
}
/**
 * Converts a Roman numeral string into its numeric integer value.
 *
 * @param roman - Standard subtractive Roman numerals; matching is case-insensitive and surrounding whitespace is ignored
 * @returns The integer value (1-3999), or NaN if invalid
 * @example
 * ```ts
 * fromRoman('MMXXVI'); // 2026
 * fromRoman('XIV'); // 14
 * fromRoman('IIII'); // NaN
 * ```
 */
export function fromRoman(roman) {
    const str = roman?.trim?.().toUpperCase();
    if (!str || !RE_ROMAN_NUMERALS.test(str))
        return NaN;
    let total = 0;
    for (let i = 0; i < str.length; i++) {
        const current = ROMAN_VALUES[str[i]] ?? 0;
        const next = ROMAN_VALUES[str[i + 1]] ?? 0;
        total += current < next ? -current : current;
    }
    return total;
}
